<script lang="ts">
  // A conversation comment or a held draft anchored to a line (by its
  // changelist:anchor marker), shown in the code without its header and
  // marker.
  import { commentKeys } from '../keys/keyboard.svelte';
  import { subject } from '../state/pr.svelte';
  import type { Conversation } from '../state/rangeHost';
  import type { ReviewDraft } from '../api/server';
  import { blobUrl, lineLabel, replaceOffDiffText, type OffDiffBody } from '../logic/offdiff';
  import { relTime } from '../logic/time';
  import { session } from '../state/session.svelte';
  import Html from './Html.svelte';
  import SnapshotChip from './SnapshotChip.svelte';
  import PendingTag from './PendingTag.svelte';
  import SaveButton from './SaveButton.svelte';
  import { onDestroy, tick } from 'svelte';
  import { loadDraft, mergeText, saveDraft } from '../state/drafts';
  import { IDLE, tellers } from '../state/pending.svelte';
  import { focusAt, handsFocusOn } from '../logic/focus';

  interface Props {
    pr: Conversation;
    parsed: OffDiffBody;
    /** A posted conversation comment, or the viewer's held draft. */
    comment?: { author: { login: string; avatarUrl?: string } | null; createdAt: string; url: string } | null;
    held?: ReviewDraft | null;
    blockKey?: string;
    showPath?: boolean;
  }
  let { pr, parsed, comment = null, held = null, blockKey = '', showPath = false }: Props = $props();

  const m = $derived(parsed.marker);
  const permalink = $derived(blobUrl(session.config?.github_web_url ?? 'https://github.com', pr.owner, pr.repo, m));
  const login = $derived(held ? pr.viewer || session.user?.login || 'you' : (comment?.author?.login ?? 'ghost'));
  const avatar = $derived(held ? session.user?.avatar_url : comment?.author?.avatarUrl);
  const at = $derived(held ? held.updated_at || held.created_at : (comment?.createdAt ?? ''));

  let editing = $state(false);
  let text = $state('');
  /** A write in flight on this held comment (saving it, an edit, deleting it): it is dimmed and its buttons are off. */
  const busy = $derived(held ? pr.writes.on(subject.draft(held.id)) : null);

  let root: HTMLDivElement | undefined = $state();
  /** Unsent edit text, kept in IndexedDB like every other comment being typed. */
  const draftKey = $derived(held ? `held:${held.id}` : '');
  /** How saving the edit stands (see `Writes.box`): the editor stays open through it, and tells it. */
  const editSave = $derived(draftKey ? pr.writes.box(draftKey) : IDLE);
  /** A write on the comment that the open editor does not tell itself: the comment is dimmed and tagged. */
  const shownBusy = $derived(editing && editSave.at === 'saving' ? null : busy);
  // svelte-ignore state_referenced_locally (the store is the note's for its life)
  const mine = tellers(pr.writes);
  onDestroy(mine.release);

  /** Counts openings of the editor: a save answered after it was closed is not the editor opened since. */
  let opened = 0;

  async function edit() {
    text = parsed.text;
    editing = true;
    opened++;
    if (!draftKey) return;
    const key = draftKey;
    const original = text;
    // An edit being sent, or offered for Retry, is not offered again (see Writes.holds).
    const saved = await loadDraft(key, { outbox: !pr.writes.holds(key) });
    // An edit left unsent earlier comes back, unless typing has started meanwhile.
    if (!editing || key !== draftKey) return;
    // An edit that was not saved is told here, with its text, and no longer by the toast.
    const failed = pr.writes.box(key);
    if (failed.at === 'failed') mine.tell(key);
    const unsent = failed.at === 'failed' ? mergeText(saved, failed.text ?? '') : saved;
    if (unsent && text === original) text = unsent;
  }

  /**
   * Close the editor (its text stays saved as a draft) and give focus back
   * to Edit (to the comment while it is being saved). `saved`: it closes
   * because the server has the edit, at a moment the person did not
   * choose, so the focus moves only if it is still in the editor (or
   * nowhere), never from wherever the person is typing by then.
   */
  async function close(saved = false) {
    const focus = focusAt(root?.querySelector('.composer'));
    editing = false;
    mine.release();
    if (saved && !handsFocusOn(focus)) return;
    await tick();
    (root?.querySelector<HTMLElement>('.held-edit') ?? root?.querySelector<HTMLElement>('.comment'))?.focus({ preventScroll: true });
  }

  // While the editor is open: a failed edit given up comes back, beside what was typed since (else it loads on Edit).
  $effect(() => {
    if (editing && draftKey) return pr.writes.listen(draftKey, (t) => (text = mergeText(text, t)));
  });

  /**
   * Save the edit. From the press the editor is saving: its button says so
   * and takes no second press. Once the server has the edit the editor
   * closes on the new text. If saving fails the editor stays as it was,
   * says why beside the button, and sends again on the next press.
   */
  async function save() {
    if (!held || !text.trim() || busy) return;
    const sent = text;
    const editor = opened;
    mine.tell(draftKey);
    if (!(await pr.editDraft(held.id, replaceOffDiffText(held.body, sent), { key: draftKey, text: sent }))) return;
    // Still the editor that sent it (not closed, or opened again, meanwhile).
    if (editing && opened === editor) void close(true);
  }

  /** Delete: the comment stays, marked as being deleted, until the server confirms. */
  function remove() {
    if (!held || busy) return;
    if (!confirm('Delete this held comment?')) return;
    void pr.discardDraft(held.id, draftKey);
  }

  // Mod+Enter sends, Esc closes (never mid-composition).
  const keys = commentKeys((c) => {
    if (c === 'submit') void save();
    else void close();
  });
</script>

<div
  bind:this={root}
  class="thread anchored"
  class:draft={!!held}
  class:is-pending={!!shownBusy}
  aria-busy={busy ? 'true' : undefined}
  data-block={blockKey}
  data-anchored={held ? 'held' : 'conversation'}
>
  <div class="comment" tabindex="-1">
    <div class="comment-head">
      {#if avatar}<img class="avatar" src={avatar} alt="" />{/if}
      <b>{login}</b>
      {#if at}<span title={at}>{relTime(at)}</span>{/if}
      {#if held}
        <span class="badge draft" title="Held on Changeling until you publish, then posted to the conversation">held draft</span>
      {:else}
        <span class="badge" title="A conversation comment that names this line">conversation</span>
      {/if}
      <!-- The snapshot chip first, as in Thread and Overview. -->
      {#if at}<SnapshotChip {pr} commit={m.commit} {at} path={m.path} line={m.side === 'RIGHT' ? m.line : null} outdated={{ anchored: true }} />{/if}
      <a class="anchor-chip" href={permalink} target="_blank" rel="noreferrer" title="This line on GitHub at {m.commit.slice(0, 7)}"
        >{showPath ? m.path + ' · ' : ''}{lineLabel(m)}{m.side === 'LEFT' ? ' (old)' : ''} ↗</a
      >
      {#if comment?.url && comment.url !== '#'}<a class="micro-link" href={comment.url} target="_blank" rel="noreferrer">on GitHub ↗</a>{/if}
    </div>
    {#if editing}
      <div class="composer" style="border-color:var(--line)">
        <!-- svelte-ignore a11y_autofocus -->
        <textarea autofocus aria-label="Edit held comment" bind:value={text} readonly={editSave.at === 'saving'} oninput={() => draftKey && saveDraft(draftKey, text)} {@attach keys}></textarea>
        <div class="row">
          <span class="spacer"></span>
          <button class="micro" onclick={() => close()}>{editSave.at === 'saving' ? 'Close' : 'Cancel'}</button>
          <SaveButton save={editSave} label="Save" disabled={!text.trim() || !!shownBusy} onsave={save} />
        </div>
      </div>
    {:else}
      <Html md={parsed.text} />
    {/if}
  </div>
  {#if held && !editing}
    <div class="thread-actions">
      <span class="resolves-later">Posts to the conversation when you publish</span>
      <span class="spacer"></span>
      {#if busy}
        <PendingTag label={busy.label} />
      {:else}
        <button class="micro held-edit" onclick={edit}>Edit</button>
        <button class="micro" onclick={remove}>Delete draft</button>
      {/if}
    </div>
  {/if}
</div>
